import React, { JSX } from 'react';
import {
	Box,
	Heading,
	VStack,
	Text,
	Container,
	SimpleGrid,
	Flex,
	Icon,
	HStack,
	useBreakpointValue,
} from '@chakra-ui/react';
import { FolderOpen, Layers, Clock, ArrowRight, ArrowDown, Download, Edit, Upload } from 'lucide-react';

interface PropsInterface {
	children?: React.ReactNode;
}

function WorkflowSection(_props: PropsInterface): JSX.Element {
	const isMobile = useBreakpointValue({ base: true, md: false });

	return (
		<Box py={48} bg="gray.50" position="relative" overflow="hidden">
			{/* Top fade from white to gray */}
			<Box
				position="absolute"
				top="0"
				left="0"
				right="0"
				h="32"
				bgGradient="to-b"
				gradientFrom="white"
				gradientTo="gray.50"
				pointerEvents="none"
				zIndex={2}
			/>
			{/* Bottom fade from gray to white */}
			<Box
				position="absolute"
				bottom="0"
				left="0"
				right="0"
				h="32"
				bgGradient="to-t"
				gradientFrom="white"
				gradientTo="gray.50"
				pointerEvents="none"
				zIndex={2}
			/>

			{/* Workflow path background */}
			<Box position="absolute" top="0" left="0" right="0" bottom="0" pointerEvents="none" zIndex={0}>
				<svg
					width="100%"
					height="100%"
					viewBox="0 0 100 110"
					preserveAspectRatio="none"
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
					}}
				>
					<defs>
						<linearGradient id="gradient1" x1="0%" y1="0%" x2="0%" y2="100%">
							<stop offset="0%" stopColor="#3182ce" />
							<stop offset="50%" stopColor="#805ad5" />
							<stop offset="100%" stopColor="#38a169" />
						</linearGradient>
						<linearGradient id="fadeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
							<stop offset="0%" stopColor="black" />
							<stop offset="10%" stopColor="white" />
							<stop offset="90%" stopColor="white" />
							<stop offset="100%" stopColor="black" />
						</linearGradient>
						<mask id="fadeMask">
							<rect x="0" y="0" width="100" height="110" fill="url(#fadeGradient)" />
						</mask>
					</defs>

					{/* Path with direction changes */}
					<path
						d="M 50 5 L 50 20 Q 50 25 45 25 L 35 25 Q 30 25 30 30 L 30 40 Q 30 45 35 45 L 45 45 Q 50 45 50 50 L 50 55 Q 50 60 55 60 L 65 60 Q 70 60 70 65 L 70 75 L 70 105"
						stroke="url(#gradient1)"
						strokeWidth="15"
						fill="none"
						strokeDasharray="60 30"
						opacity="0.3"
						mask="url(#fadeMask)"
						vectorEffect="non-scaling-stroke"
					>
						<animate
							attributeName="stroke-dashoffset"
							from="0"
							to="-90"
							dur="1s"
							repeatCount="indefinite"
						/>
					</path>
				</svg>
			</Box>

			<Container maxW="6xl" position="relative" zIndex={1}>
				<VStack textAlign="center" gap={0} mb={16}>
					<Text
						fontSize="11px"
						fontWeight="500"
						textTransform="uppercase"
						letterSpacing="0.22em"
						color="gray.400"
					>
						Workflow
					</Text>
					<Heading
						mt={5}
						mb={8}
						maxW="2xl"
						fontSize={{ base: '2.25rem', md: '3.75rem' }}
						fontWeight={400}
						lineHeight={1}
						letterSpacing="-0.035em"
					>
						Organize, Create, Iterate
					</Heading>
					<Text maxW="2xl" fontSize={{ base: '1.125rem', md: '1.25rem' }} color="gray.600" lineHeight={1.6}>
						Manage your creative work through project-based workflows with full asset control and timeline
						tracking.
					</Text>
				</VStack>

				<SimpleGrid columns={{ base: 1, md: 3 }} gap={8} justifyItems="center">
					<Box
						p={8}
						shadow="md"
						rounded="2xl"
						border="1px solid"
						borderColor="gray.100"
						transition="all 0.3s"
						_hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
						h="full"
						position="relative"
						bg="white"
						opacity={1}
					>
						<Box
							position="absolute"
							top={6}
							right={6}
							fontSize="32px"
							fontWeight="500"
							lineHeight="none"
							letterSpacing="-0.04em"
							bg="gradientPrimary"
							bgClip="text"
							color="transparent"
							opacity={0.3}
						>
							01
						</Box>
						<Flex
							w={12}
							h={12}
							bgGradient="to-br"
							gradientFrom="primary"
							gradientTo="secondary"
							rounded="xl"
							align="center"
							justify="center"
							mb={4}
						>
							<Icon as={FolderOpen} boxSize={6} color="white" />
						</Flex>
						<Heading size="md" mb={3} color="gray.900">
							Create Projects
						</Heading>
						<Text color="gray.600" fontSize="sm" lineHeight={1.6}>
							Organize your work into multiple project folders. Each project is a dedicated workspace.
						</Text>
					</Box>

					<Box
						p={8}
						shadow="md"
						rounded="2xl"
						border="1px solid"
						borderColor="gray.100"
						transition="all 0.3s"
						_hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
						h="full"
						position="relative"
						bg="white"
						opacity={1}
					>
						<Box
							position="absolute"
							top={6}
							right={6}
							fontSize="32px"
							fontWeight="500"
							lineHeight="none"
							letterSpacing="-0.04em"
							bg="gradientPrimary"
							bgClip="text"
							color="transparent"
							opacity={0.3}
						>
							02
						</Box>
						<Flex
							w={12}
							h={12}
							bgGradient="to-br"
							gradientFrom="primary"
							gradientTo="secondary"
							rounded="xl"
							align="center"
							justify="center"
							mb={4}
						>
							<Icon as={Layers} boxSize={6} color="white" />
						</Flex>
						<Heading size="md" mb={3} color="gray.900">
							Generate & Reference Assets
						</Heading>
						<Text color="gray.600" fontSize="sm" lineHeight={1.6}>
							Generate images and videos within projects. Reference existing assets, modify them.
						</Text>
					</Box>

					<Box
						p={8}
						shadow="md"
						rounded="2xl"
						border="1px solid"
						borderColor="gray.100"
						transition="all 0.3s"
						_hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
						h="full"
						position="relative"
						bg="white"
						opacity={1}
					>
						<Box
							position="absolute"
							top={6}
							right={6}
							fontSize="32px"
							fontWeight="500"
							lineHeight="none"
							letterSpacing="-0.04em"
							bg="gradientPrimary"
							bgClip="text"
							color="transparent"
							opacity={0.3}
						>
							03
						</Box>
						<Flex
							w={12}
							h={12}
							bgGradient="to-br"
							gradientFrom="primary"
							gradientTo="secondary"
							rounded="xl"
							align="center"
							justify="center"
							mb={4}
						>
							<Icon as={Clock} boxSize={6} color="white" />
						</Flex>
						<Heading size="md" mb={3} color="gray.900">
							Track Your Timeline
						</Heading>
						<Text color="gray.600" fontSize="sm" lineHeight={1.6}>
							Your project timeline is your workflow. Track progress and iterate on creations.
						</Text>
					</Box>
				</SimpleGrid>

				<VStack mt={20} gap={8}>
					<VStack textAlign="center" gap={0}>
						<Heading size="lg" mb={4} color="gray.900">
							Asset Workflow
						</Heading>
						<Text color="gray.600" fontSize="md" maxW="2xl">
							Generate or modify assets, then seamlessly integrate them into your favorite tools.
						</Text>
					</VStack>

					{isMobile ? (
						<VStack gap={4} align="center">
							<Flex
								align="center"
								gap={4}
								p={6}
								bg="white"
								rounded="2xl"
								shadow="md"
								border="1px solid"
								borderColor="gray.100"
								minW="auto"
							>
								<Flex
									w={10}
									h={10}
									bgGradient="to-br"
									gradientFrom="primary"
									gradientTo="secondary"
									rounded="lg"
									align="center"
									justify="center"
									flexShrink={0}
								>
									<Icon as={Upload} boxSize={5} color="white" />
								</Flex>
								<VStack align="start" gap={0}>
									<Text fontWeight="600" color="gray.900" fontSize="sm">
										Drag In / Generate
									</Text>
									<Text color="gray.500" fontSize="xs">
										Import or create
									</Text>
								</VStack>
							</Flex>
							<Icon as={ArrowDown} boxSize={6} color="gray.300" />
							<Flex
								align="center"
								gap={4}
								p={6}
								bg="white"
								rounded="2xl"
								shadow="md"
								border="1px solid"
								borderColor="gray.100"
								minW="auto"
							>
								<Flex
									w={10}
									h={10}
									bgGradient="to-br"
									gradientFrom="primary"
									gradientTo="secondary"
									rounded="lg"
									align="center"
									justify="center"
									flexShrink={0}
								>
									<Icon as={Edit} boxSize={5} color="white" />
								</Flex>
								<VStack align="start" gap={0}>
									<Text fontWeight="600" color="gray.900" fontSize="sm">
										Edit Asset
									</Text>
									<Text color="gray.500" fontSize="xs">
										Modify & refine
									</Text>
								</VStack>
							</Flex>
							<Icon as={ArrowDown} boxSize={6} color="gray.300" />
							<Flex
								align="center"
								gap={4}
								p={6}
								bg="white"
								rounded="2xl"
								shadow="md"
								border="1px solid"
								borderColor="gray.100"
								minW="auto"
							>
								<Flex
									w={10}
									h={10}
									bgGradient="to-br"
									gradientFrom="primary"
									gradientTo="secondary"
									rounded="lg"
									align="center"
									justify="center"
									flexShrink={0}
								>
									<Icon as={Download} boxSize={5} color="white" />
								</Flex>
								<VStack align="start" gap={0}>
									<Text fontWeight="600" color="gray.900" fontSize="sm">
										Drag Out
									</Text>
									<Text color="gray.500" fontSize="xs">
										Export to tools
									</Text>
								</VStack>
							</Flex>
						</VStack>
					) : (
						<HStack gap={8} align="center" justify="center">
							<Flex
								align="center"
								gap={4}
								p={6}
								bg="white"
								rounded="2xl"
								shadow="md"
								border="1px solid"
								borderColor="gray.100"
								minW="200px"
							>
								<Flex
									w={10}
									h={10}
									bgGradient="to-br"
									gradientFrom="primary"
									gradientTo="secondary"
									rounded="lg"
									align="center"
									justify="center"
									flexShrink={0}
								>
									<Icon as={Upload} boxSize={5} color="white" />
								</Flex>
								<VStack align="start" gap={0}>
									<Text fontWeight="600" color="gray.900" fontSize="sm">
										Drag In / Generate
									</Text>
									<Text color="gray.500" fontSize="xs">
										Import or create
									</Text>
								</VStack>
							</Flex>
							<Icon as={ArrowRight} boxSize={6} color="gray.300" flexShrink={0} />
							<Flex
								align="center"
								gap={4}
								p={6}
								bg="white"
								rounded="2xl"
								shadow="md"
								border="1px solid"
								borderColor="gray.100"
								minW="200px"
							>
								<Flex
									w={10}
									h={10}
									bgGradient="to-br"
									gradientFrom="primary"
									gradientTo="secondary"
									rounded="lg"
									align="center"
									justify="center"
									flexShrink={0}
								>
									<Icon as={Edit} boxSize={5} color="white" />
								</Flex>
								<VStack align="start" gap={0}>
									<Text fontWeight="600" color="gray.900" fontSize="sm">
										Edit Asset
									</Text>
									<Text color="gray.500" fontSize="xs">
										Modify & refine
									</Text>
								</VStack>
							</Flex>
							<Icon as={ArrowRight} boxSize={6} color="gray.300" flexShrink={0} />
							<Flex
								align="center"
								gap={4}
								p={6}
								bg="white"
								rounded="2xl"
								shadow="md"
								border="1px solid"
								borderColor="gray.100"
								minW="200px"
							>
								<Flex
									w={10}
									h={10}
									bgGradient="to-br"
									gradientFrom="primary"
									gradientTo="secondary"
									rounded="lg"
									align="center"
									justify="center"
									flexShrink={0}
								>
									<Icon as={Download} boxSize={5} color="white" />
								</Flex>
								<VStack align="start" gap={0}>
									<Text fontWeight="600" color="gray.900" fontSize="sm">
										Drag Out
									</Text>
									<Text color="gray.500" fontSize="xs">
										Export to tools
									</Text>
								</VStack>
							</Flex>
						</HStack>
					)}

					<HStack gap={4} mt={4} flexWrap="wrap" justify="center">
						<Text color="gray.500" fontSize="sm" fontWeight="500">
							Compatible with:
						</Text>
						<Text color="gray.600" fontSize="sm">
							Video Editing
						</Text>
						<Text color="gray.300">•</Text>
						<Text color="gray.600" fontSize="sm">
							Game Development
						</Text>
						<Text color="gray.300">•</Text>
						<Text color="gray.600" fontSize="sm">
							Web Development
						</Text>
						<Text color="gray.300">•</Text>
						<Text color="gray.600" fontSize="sm">
							Design Tools
						</Text>
						<Text color="gray.300">•</Text>
						<Text color="gray.600" fontSize="sm">
							Marketing Platforms
						</Text>
					</HStack>
				</VStack>
			</Container>
		</Box>
	);
}

export default WorkflowSection;
